import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Building2, Globe2, LineChart, ShieldCheck, Stethoscope, Briefcase, Database, Network } from 'lucide-react';
export function About() {
  return <div className="w-full" data-id="element-45">
      {/* SECTION 1: HERO CHAMBER */}
      <section className="relative bg-navy-900 text-white overflow-hidden min-h-[90vh] flex items-center" data-id="element-46">
        {/* Sharper background with radial gradient focus */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.05)_0%,transparent_70%)]" data-id="element-47"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-30" data-id="element-48"></div>
        <div className="absolute top-0 right-0 w-2/5 h-full bg-navy-800 border-l border-white/10 hidden lg:block shadow-2xl" data-id="element-49"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 w-full py-24" data-id="element-50">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-51">
            <div className="lg:col-span-7 order-2 lg:order-1" data-id="element-52">
              <motion.div initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-53">
                <div className="inline-block px-5 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold tracking-widest uppercase mb-8 shadow-[0_0_15px_rgba(16,185,129,0.15)]" data-id="element-54">
                  Enterprise Leadership & Strategy
                </div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 drop-shadow-lg text-emerald-400" data-id="element-55">
                  Robert G. Frank, Ph.D.
                </h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6" data-id="element-56">
                  {['Chief Executive', 'Institutional Strategist', 'Board Director'].map((title, i) => <Fragment key={title} data-id="element-57">
                      {i > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" data-id="element-58"></span>}
                      <span className="text-lg md:text-xl text-slate-200 font-semibold tracking-wide" data-id="element-59">
                        {title}
                      </span>
                    </Fragment>)}
                </div>
                <p className="text-xl md:text-2xl font-medium text-white leading-relaxed mb-8 max-w-3xl drop-shadow-md" data-id="element-60">
                  Directing Fiduciary &amp; Operational Governance Over $3B+
                  Enterprise Ecosystems.
                </p>
                <div className="text-lg text-slate-400 mb-12 max-w-3xl leading-relaxed space-y-6" data-id="element-61">
                  <p data-id="element-62">
                    With a distinguished career spanning over three decades, Dr.
                    Robert G. Frank has engineered massive-scale institutional
                    growth, bridging the gap between complex macro-policy and
                    measurable, bottom-line financial execution. From his
                    foundational work as a Professor and Dean to his
                    transformative tenures as Provost and President, he has
                    consistently turned systemic institutional risk into
                    sustainable market dominance.
                  </p>
                  <p data-id="element-63">
                    His architectural vision has capitalized multibillion-dollar
                    ecosystems, pioneered Responsibility-Centered Management
                    (RCM) models, and launched entirely new academic and
                    clinical pillars. Today, as a premier consultant, he
                    leverages this unparalleled background to advise university
                    leadership and corporate boards on ecosystem architecture,
                    knowledge commercialization, and high-impact Public-Private
                    Partnerships (P3).
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-6" data-id="element-64">
                  <Link to="/experiences" className="px-10 py-5 bg-emerald-500 text-navy-900 font-bold uppercase tracking-widest hover:bg-emerald-400 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] flex items-center justify-center gap-3" data-id="element-65">
                    Review Board Credentials
                    <ArrowRight size={20} data-id="element-66" />
                  </Link>
                  <Link to="/contributions" className="px-10 py-5 bg-navy-800/50 border border-slate-500 text-white font-bold uppercase tracking-widest hover:bg-white/10 transition-colors flex items-center justify-center backdrop-blur-sm" data-id="element-67">
                    Read Leadership Philosophy
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Profile Portrait */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end" data-id="element-68">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.9,
              ease: 'easeOut',
              delay: 0.15
            }} className="relative" data-id="element-69">
                {/* Decorative rings */}
                <div className="absolute -inset-4 rounded-full border border-emerald-500/20" data-id="element-70"></div>
                <div className="absolute -inset-8 rounded-full border border-white/5" data-id="element-71"></div>

                <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[26rem] lg:h-[26rem] rounded-full p-1.5 bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-[0_0_50px_rgba(16,185,129,0.25)]" data-id="element-72">
                  <div className="w-full h-full rounded-full p-1 bg-navy-900" data-id="element-73">
                    <img src="https://cdn.magicpatterns.com/uploads/jTmvvKKbmKBDWKdPaAxJBX/Robert_Frank-Picsart-AiImageEnhancer.png" alt="Portrait of Dr. Robert G. Frank" className="w-full h-full rounded-full object-cover object-top" data-id="element-74" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: EXECUTIVE METRIC TICKER */}
      <section className="bg-navy-800 py-20 border-y border-white/10 relative overflow-hidden" data-id="element-75">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-transparent to-navy-900 opacity-50" data-id="element-76"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" data-id="element-77">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 divide-y md:divide-y-0 md:divide-x divide-white/10" data-id="element-78">
            {[{
            number: '$3.1B',
            label: 'Fiduciary Governance Managed'
          }, {
            number: '1,233%',
            label: 'Fiscal Expansion & Research Growth'
          }, {
            number: '500%',
            label: 'Student Enrollment Pipelines Scaled'
          }, {
            number: '$9.5M+',
            label: 'Competitive External Grant Funding Secured'
          }].map((metric, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1,
            duration: 0.6
          }} className={`pt-8 md:pt-0 ${idx !== 0 ? 'md:pl-8 lg:pl-12' : ''}`} data-id="element-79">
                <div className="font-serif text-5xl lg:text-6xl text-white mb-4 tracking-tight drop-shadow-md" data-id="element-80">
                  {metric.number}
                </div>
                <div className="text-emerald-400 text-sm font-bold uppercase tracking-widest leading-snug" data-id="element-81">
                  {metric.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: CORE COMPETENCY GRID */}
      <section className="py-28 bg-slate-50" data-id="element-82">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-83">
          <div className="mb-20 text-center" data-id="element-84">
            <h2 className="font-serif text-4xl md:text-6xl text-navy-900 mb-6" data-id="element-85">
              Strategic Capabilities
            </h2>
            <div className="w-32 h-1.5 bg-emerald-500 mx-auto" data-id="element-86"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-87">
            {[{
            icon: ShieldCheck,
            title: 'Fiduciary Oversight & Treasury Management'
          }, {
            icon: Building2,
            title: 'Public-Private Partnerships (P3) Alliances'
          }, {
            icon: Globe2,
            title: 'Macro-Economic & Regional Development'
          }, {
            icon: BarChart3,
            title: 'Responsibility-Centered Management (RCM)'
          }, {
            icon: Stethoscope,
            title: 'Health System Architecture & Policy Strategy'
          }, {
            icon: LineChart,
            title: 'Corporate Restructuring & Turnarounds'
          }, {
            icon: Network,
            title: 'Clinical Operations Governance'
          }, {
            icon: Briefcase,
            title: 'Portfolio Diversification & Product Launch'
          }, {
            icon: Database,
            title: 'Enterprise-Wide Data Analytics'
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05,
            duration: 0.4
          }} className="p-10 border border-slate-200 hover:border-emerald-500 hover:shadow-2xl transition-all group bg-white cursor-default relative overflow-hidden" data-id="element-88">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110" data-id="element-89"></div>
                <item.icon className="w-12 h-12 text-slate-400 group-hover:text-emerald-500 transition-colors mb-8 relative z-10" strokeWidth={1.5} data-id="element-90" />
                <h3 className="font-serif text-2xl text-navy-900 font-medium leading-snug relative z-10" data-id="element-91">
                  {item.title}
                </h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: SIGNATURE LEADERSHIP IMPERATIVES */}
      <section className="py-28 bg-white border-t border-slate-200" data-id="element-92">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-93">
          <div className="mb-24 text-center" data-id="element-94">
            <h2 className="font-serif text-4xl md:text-6xl text-navy-900 mb-6" data-id="element-95">
              Leadership Philosophy
            </h2>
            <div className="w-32 h-1.5 bg-emerald-500 mx-auto" data-id="element-96"></div>
          </div>

          <div className="space-y-32" data-id="element-97">
            {[{
            title: 'Fiduciary Governance & Systems Architecture',
            desc: 'Focus on absolute strategic governance, organization-wide goal accountability, and decentralized financial systems (RCM).',
            number: '01'
          }, {
            title: 'Radical Expansion & Portfolio Diversification',
            desc: 'Focus on continuous innovation, launching new institutional pillars from scratch, and capitalizing on macro-sector demands.',
            number: '02'
          }, {
            title: 'Data-Driven Performance Optimization',
            desc: 'Focus on enterprise-wide predictive data analytics platforms to streamline throughput efficiencies and elevate quality benchmarks.',
            number: '03'
          }, {
            title: 'Public-Private Partnerships (P3) & Alliances',
            desc: 'Focus on bridging corporate, civic, health, and legislative bodies to turn large institutions into regional economic engines (e.g., Innovate ABQ blueprint).',
            number: '04'
          }].map((pillar, idx) => <div key={idx} className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-24 items-center`} data-id="element-98">
                <div className="lg:w-1/2 w-full" data-id="element-99">
                  <div className="aspect-[4/3] bg-navy-900 relative overflow-hidden flex items-center justify-center shadow-2xl" data-id="element-100">
                    <div className="absolute inset-0 bg-grid-pattern opacity-30" data-id="element-101"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent" data-id="element-102"></div>
                    <span className="font-serif text-[12rem] text-white/5 font-bold drop-shadow-2xl" data-id="element-103">
                      {pillar.number}
                    </span>
                  </div>
                </div>
                <div className="lg:w-1/2 w-full" data-id="element-104">
                  <div className="text-emerald-500 font-bold tracking-widest uppercase mb-6 text-sm flex items-center gap-4" data-id="element-105">
                    <span className="w-12 h-px bg-emerald-500" data-id="element-106"></span>
                    Imperative {pillar.number}
                  </div>
                  <h3 className="font-serif text-4xl md:text-5xl text-navy-900 mb-8 leading-tight" data-id="element-107">
                    {pillar.title}
                  </h3>
                  <p className="text-xl text-slate-600 leading-relaxed" data-id="element-108">
                    {pillar.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}